// src/interactiveUGC/components/SceneComponent.jsx
import * as React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import MapIcon from '@mui/icons-material/Map';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import useMediaQuery from '@mui/material/useMediaQuery';
import Collapse from '@mui/material/Collapse';
import { useTheme } from '@mui/material/styles';
import { Handle, Position } from '@xyflow/react';
import { GraphCtx } from './InteractiveEditor';
import UndoIcon from '@mui/icons-material/Undo';
import CharacterSelect from './CharaterSelect';
import { addCharacter, getCharacters } from '../../utils/apis/interactiveStoriesAPI';
import { ensureAssetsManifest, getManifest, resolveBackgroundUrlByIndex } from '../../utils/assetsManifest';
import Typography from '@mui/material/Typography';
import { t } from '@lingui/macro';

// ───────────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────────
const asInt = (v, d = 0) => {
  const n = Number.parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : d;
};

const trimStr = (s) => (typeof s === 'string' ? s.trim() : '');

const cssEscape = (s) => {
  try {
    return (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(String(s)) : String(s);
  } catch {
    return String(s);
  }
};

// FLIP helpers (animate all affected items, not just the moved one)
function measureListChildren(ref, attrName) {
  const map = new Map();
  const root = ref.current;
  if (!root) return map;
  root.querySelectorAll(`[${attrName}]`).forEach((el) => {
    const key = el.getAttribute(attrName);
    if (!key) return;
    map.set(key, el.getBoundingClientRect());
  });
  return map;
}

function flipAnimateAll(ref, attrName, beforeRects, duration = 180) {
  requestAnimationFrame(() => {
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll(`[${attrName}]`).forEach((el) => {
      const key = el.getAttribute(attrName);
      if (!key) return;
      const before = beforeRects.get(key);
      if (!before) return;
      const after = el.getBoundingClientRect();
      const dy = before.top - after.top;
      if (Math.abs(dy) < 0.5) return;

      el.style.willChange = 'transform';
      el.style.transform = `translateY(${dy}px)`;
      el.style.transition = 'transform 0s';
      requestAnimationFrame(() => {
        el.style.transition = `transform ${duration}ms ease`;
        el.style.transform = 'translateY(0)';
        const cleanup = () => {
          el.style.transition = '';
          el.style.transform = '';
          el.style.willChange = '';
          el.removeEventListener('transitionend', cleanup);
        };
        el.addEventListener('transitionend', cleanup);
        setTimeout(cleanup, duration + 80);
      });
    });
  });
}

function withStableKey(c) {
  if (c && c.__key) return c;
  return { ...c, __key: `k_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6)}` };
}

function deriveConnectionsFromData(data) {
  // Prefer unified shape if present
  if (data?.lastScene) return [];
  if (Array.isArray(data?.connections)) {
    return data.connections.map(c => withStableKey({
      type: c?.type === 'existing' && c?.childId ? 'existing' : 'new',
      childId: c?.childId ? String(c.childId) : '',
      text: trimStr(c?.text) || '',
    }));
  }

  const mode = data?.mode || 'none';
  const cont = data?.continue;
  const choices = Array.isArray(data?.choices) ? data.choices : [];

  if (mode === 'none') return [];
  if (mode === 'continue') {
    if (cont?.type === 'existing' && cont?.childId) {
      return [withStableKey({ type: 'existing', childId: String(cont.childId), text: '' })];
    }
    return [withStableKey({ type: 'new', childId: '', text: '' })];
  }
  if (mode === 'choices') {
    if (choices.length) {
      return choices.map(c => withStableKey({
        type: (c?.type === 'existing' && c?.childId) ? 'existing' : 'new',
        childId: c?.childId ? String(c.childId) : '',
        text: trimStr(c?.text) || '',
      }));
    }
    // Fallback: show two empty choices to nudge UX when mode was choices but array missing
    return [withStableKey({ type: 'new', childId: '', text: '' }), withStableKey({ type: 'new', childId: '', text: '' })];
  }
  return [];
}

// ★ Enforce choice text ≤ 60 and resanitize targets; keep __key
function sanitizeConnections(connections, { maxChildren, selfId, allSceneIds }) {
  let list = (Array.isArray(connections) ? connections : []).map(c => {
    const base = {
      __key: c?.__key || `k_${Math.random().toString(36).slice(2)}`,
      type: c?.type === 'existing' ? 'existing' : 'new',
      childId: c?.childId ? String(c.childId) : '',
      text: typeof c?.text === 'string' ? c.text.slice(0, 60) : '',
    };
    return base;
  });

  // drop self-links & invalid targets ONLY when there is a childId
  list = list.filter(c => {
    if (c.type === 'existing' && c.childId) {
      if (c.childId === selfId) return false;
      if (allSceneIds && !allSceneIds.has(c.childId)) return false;
    }
    return true;
  });

  // dedupe existing targets (first wins)
  const seen = new Set();
  const deduped = [];
  for (const c of list) {
    if (c.type === 'existing' && c.childId) {
      if (seen.has(c.childId)) continue;
      seen.add(c.childId);
    }
    deduped.push(c);
  }

  // cap total rows
  return deduped.slice(0, Math.max(0, asInt(maxChildren, 3)));
}

// generic 3s deletion scheduler (rowKey-based)
function useDeferredDeletion(listRef, removeByKey, attrName = 'data-row-key') {
  const [pending, setPending] = React.useState({}); // key -> timeoutId

  const schedule = React.useCallback((rowKey) => {
    if (pending[rowKey]) return;
    const timeoutId = window.setTimeout(() => {
      const beforeRects = measureListChildren(listRef, attrName);
      removeByKey(rowKey);
      setPending(p => {
        const copy = { ...p };
        delete copy[rowKey];
        return copy;
      });

      // 🔁 Run FLIP immediately; it already uses rAF internally
      flipAnimateAll(listRef, attrName, beforeRects);
    }, 3000);

    setPending(p => ({ ...p, [rowKey]: timeoutId }));
  }, [pending, listRef, removeByKey, attrName]);

  const undo = React.useCallback((rowKey) => {
    const t = pending[rowKey];
    if (t) {
      window.clearTimeout(t);
      setPending(p => {
        const copy = { ...p };
        delete copy[rowKey];
        return copy;
      });
    }
  }, [pending]);

  const isPending = React.useCallback((rowKey) => !!pending[rowKey], [pending]);

  return { schedule, undo, isPending };
}

// progress bar (CSS, 3s)
function DeletingBar() {
  return (
    <Box sx={{
      mt: 0.5,
      height: 4,
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: 'action.hover',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        width: '0%',
        bgcolor: 'error.main',
        '@keyframes fill3': { from: { width: '0%' }, to: { width: '100%' } },
        animation: 'fill3 3s linear forwards',
      }
    }} />
  );
}

function useImageMeta(url) {
  const [meta, setMeta] = React.useState({ w: 0, h: 0, ready: false });
  React.useEffect(() => {
    if (!url) { setMeta({ w: 0, h: 0, ready: false }); return; }
    let mounted = true;
    const img = new Image();
    img.onload = () => { if (mounted) setMeta({ w: img.naturalWidth, h: img.naturalHeight, ready: true }); };
    img.onerror = () => { if (mounted) setMeta({ w: 0, h: 0, ready: false }); };
    img.src = url;
    return () => { mounted = false; };
  }, [url]);
  return meta;
}
function BackgroundThumb({ idx, selected, onSelect }) {
  const [src, setSrc] = React.useState('');
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const url = await resolveBackgroundUrlByIndex(idx);
        if (alive) setSrc(url || '');
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [idx]);

  const meta = useImageMeta(src);
  const ratio = meta.ready ? `${meta.w} / ${meta.h}` : '9 / 16';

  return (
    <Box
      onClick={() => onSelect?.(idx)}
      role="button"
      aria-label={t`Background ${idx}`}
      sx={{

        width: 88,
        aspectRatio: ratio,
        borderRadius: 1.25,
        border: (t) => `2px solid ${selected ? t.palette.primary.main : t.palette.divider}`,
        boxShadow: selected ? 2 : 0,
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: 2, borderColor: 'primary.main' },
        bgcolor: src ? 'transparent' : 'action.hover',
      }}
    >
      {src ? (
        <Box
          component="img"
          alt={t`Background ${idx}`}
          src={src}
          loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Box sx={{
          position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
          fontSize: 12, color: 'text.secondary'
        }}>
          …
        </Box>
      )}
    </Box>
  );
}

function BackgroundSelect({ value, onChange }) {
  const [count, setCount] = React.useState(0);
  const [previewUrl, setPreviewUrl] = React.useState('');
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await ensureAssetsManifest();
        const m = getManifest();
        const c = Array.isArray(m?.backgrounds) ? m.backgrounds.length : 0;
        if (alive) setCount(c);
        const v = Number(value || 0);
        if (!v || v < 1 || (c && v > c)) onChange?.(Math.min(Math.max(1, v || 1), c || 1));
      } catch { }
    })();
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const v = Math.max(1, Number(value || 1));
        const u = await resolveBackgroundUrlByIndex(v);
        if (alive) setPreviewUrl(u || '');
      } catch { }
    })();
    return () => { alive = false; };
  }, [value]);

  const indices = React.useMemo(
    () => Array.from({ length: count }, (_, i) => i + 1),
    [count]
  );

  const current = Math.max(1, Math.min(Number(value || 1), count || 1));
  const meta = useImageMeta(previewUrl);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ fontWeight: 600 }}>{t`Background`}</Box>
        <Chip size="small" label={`${current}/${count || 0}`} />
      </Box>

      {!expanded && (
        <Box
          onClick={() => setExpanded(true)}
          role="button"
          aria-label={t`Change background`}
          sx={{
            width: { xs: '100%', sm: 168 },
            aspectRatio: '16 / 9',
            borderRadius: 1.5,
            border: (t) => `1px solid ${t.palette.divider}`,
            overflow: 'hidden',
            cursor: 'pointer',
            position: 'relative',
            bgcolor: previewUrl ? 'transparent' : 'action.hover',
            boxShadow: 1,
            transition: 'box-shadow 160ms ease',
            '&:hover': { boxShadow: 2 },
          }}
        >
          {previewUrl ? (
            <Box component="img" alt="background preview" src={previewUrl}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'text.secondary', fontSize: 12 }}>
              {t`Loading…`}
            </Box>
          )}

          <IconButton
            size="small"
            title={t`Expand backgrounds`}
            aria-label={t`Expand backgrounds`}
            onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
            sx={{
              position: 'absolute',
              right: 6,
              bottom: 6,
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: (t) => `1px solid ${t.palette.divider}`,
              color: 'text.secondary',
              boxShadow: 0,
              '&:hover': { boxShadow: 1 },
            }}
          >
            <ArrowDownwardIcon fontSize="inherit" />
          </IconButton>
        </Box>
      )}

      <Collapse in={expanded} appear mountOnEnter unmountOnExit timeout={220}>
        <Box sx={{ mt: 1 }}>
          <Box sx={{
            mb: 1,
            width: '100%',
            borderRadius: 1.5,
            border: (t) => `1px solid ${t.palette.divider}`,
            overflow: 'hidden',
            aspectRatio: meta.ready ? `${meta.w} / ${meta.h}` : '9 / 16',
            bgcolor: previewUrl ? 'transparent' : 'action.hover',
            position: 'relative',
            transition: 'aspect-ratio 200ms ease',
          }}>
            {previewUrl ? (
              <Box component="img" alt="background preview" src={previewUrl}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'text.secondary', fontSize: 13 }}>
                {t`Loading…`}
              </Box>
            )}
          </Box>

          {/* Thumbnails row(s) — centered + space-between */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              maxHeight: 320,
              overflowY: 'auto',
              borderRadius: 1,
              p: 0.5,
              border: (t) => `1px dashed ${t.palette.divider}`,
            }}
          >
            {indices.map((idx) => (
              <BackgroundThumb
                key={idx}
                idx={idx}
                selected={Number(current) === idx}
                onSelect={(v) => {
                  onChange?.(Number(v));
                  setExpanded(false);
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
            <IconButton
              size="small"
              title={t`Collapse backgrounds`}
              aria-label={t`Collapse backgrounds`}
              onClick={() => setExpanded(false)}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: (t) => `1px solid ${t.palette.divider}`,
                color: 'text.primary',
                boxShadow: 0,
                '&:hover': { boxShadow: 1 },
                '&.Mui-disabled': { color: 'text.disabled' },
              }}
            >
              <ArrowUpwardIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}


// ───────────────────────────────────────────────────────────────────────────────
// Lines Editor
// ───────────────────────────────────────────────────────────────────────────────
function LinesEditor({ lines, setLines, characters, onCreateCharacter }) {
  const listRef = React.useRef(null);
  const speakerOptions = Array.isArray(characters) ? characters : [];

  const removeByKey = React.useCallback((rowKey) => {
    setLines((prev) => prev.filter((ln, i) => String(ln?.id ?? i) !== rowKey));
  }, [setLines]);

  const {
    schedule: scheduleDelete,
    undo: undoDelete,
    isPending,
  } = useDeferredDeletion(listRef, removeByKey, "data-line-id");

  const addLine = () => {
    const id = `l${Date.now().toString(36)}${Math.floor(Math.random() * 1e3)}`;
    setLines((prev) => [...prev, { id, speaker: "Narrator", text: "" }]);
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const move = (from, to) => {
    const beforeRects = measureListChildren(listRef, 'data-line-id');
    setLines(prev => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [row] = next.splice(from, 1);
      next.splice(to, 0, row);
      return next;
    });

    // ✅ No extra setTimeout – FLIP handles rAF itself
    flipAnimateAll(listRef, 'data-line-id', beforeRects);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ fontWeight: 600, mb: 1 }}>{t`Lines`}</Box>

      <Box ref={listRef}>
        {(lines || []).map((ln, idx) => {
          const rowKey = String(ln?.id ?? idx);
          const pending = isPending(rowKey);
          const textValue = ln.text || "";
          const trimmed = textValue.trim();
          const isEmpty = trimmed.length === 0;

          const baseLineLabel = t`Line text`;
          const lineLabel = isEmpty
            ? t`Line text – should not be empty`
            : baseLineLabel;


          return (
            <Box
              key={ln.id || idx}
              data-line-id={rowKey}
              sx={(t) => ({
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "160px 1fr auto" },
                gap: { xs: 1, sm: 1.25 },
                alignItems: "center",
                mb: 1.25,
                p: 1,
                borderRadius: 1,
                borderWidth: 1,
                borderStyle: "solid",
                // 🔴 subtle outline only when empty – no fill
                borderColor: isEmpty
                  ? t.palette.error.main
                  : t.palette.divider,
                bgcolor: "transparent",
                opacity: pending ? 0.65 : 1,
                transform: pending ? "translateX(6px)" : "none",
                transition:
                  "opacity 160ms ease, transform 160ms ease, border-color 160ms ease",
              })}
            >
              <CharacterSelect
                options={speakerOptions}
                value={ln.speaker || t`Narrator`}
                onChange={(id) => updateLine(idx, { speaker: id })}
                disabled={pending}
                onCreateCharacter={onCreateCharacter}
                label={t`Speaker`}
                size="small"
              />

              {/* Line text + inline char counter */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                }}
              >
                <TextField
                  size="small"
                  // ⚠️ Warning lives in the label/placeholder that shrinks to outline
                  label={lineLabel}
                  placeholder={isEmpty ? t`Should not be empty` : ""}

                  margin="dense"
                  value={textValue}
                  onChange={(e) =>
                    updateLine(idx, { text: e.target.value.slice(0, 160) })
                  }
                  sx={(t) => ({
                    flexGrow: 1,
                    minWidth: 0,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: isEmpty
                          ? t.palette.warning.main
                          : t.palette.divider,
                      },
                      "&:hover fieldset": {
                        borderColor: isEmpty
                          ? t.palette.warning.dark
                          : t.palette.text.secondary,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: isEmpty
                          ? t.palette.warning.dark
                          : t.palette.callToAction?.main ||
                          t.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                  })}
                  disabled={pending}
                  inputProps={{ maxLength: 160 }}
                />
                <Typography
                  variant="caption"
                  sx={(t) => ({
                    flexShrink: 0,
                    minWidth: 56,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    color: isEmpty
                      ? t.palette.warning.main
                      : t.palette.text.secondary,
                  })}
                >
                  {textValue.length}/160
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  mt: { xs: 0.25, sm: 0 },
                }}
              >
                <IconButton
                  size="small"
                  title={t`Move up`}
                  onClick={() => move(idx, idx - 1)}
                  disabled={pending}
                  sx={{
                    color: "text.primary",
                    "&.Mui-disabled": { color: "text.disabled" },
                  }}
                >
                  <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>

                <IconButton
                  size="small"
                  title="Move down"
                  onClick={() => move(idx, idx + 1)}
                  disabled={pending}
                  sx={{
                    color: "text.primary",
                    "&.Mui-disabled": { color: "text.disabled" },
                  }}
                >
                  <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>

                {!pending ? (
                  <IconButton
                    size="small"
                    title="Remove"
                    onClick={() => scheduleDelete(rowKey)}
                    sx={{
                      color: "text.primary",
                      "&.Mui-disabled": { color: "text.disabled" },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    title="Undo delete"
                    onClick={() => undoDelete(rowKey)}
                    sx={{
                      color: "text.primary",
                      "&.Mui-disabled": { color: "text.disabled" },
                    }}
                  >
                    <UndoIcon fontSize="inherit" />
                  </IconButton>
                )}
              </Box>

              {pending && <DeletingBar />}
            </Box>
          );
        })}
      </Box>

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={addLine}
        sx={{ mt: 0.5, width: { xs: "100%", sm: "auto" } }}
      >
        {t`Add line`}
      </Button>
    </Box>
  );
}


// ───────────────────────────────────────────────────────────────────────────────
// Connections Editor (continue/choices)
// ───────────────────────────────────────────────────────────────────────────────
function ConnectionsEditor({
  id,
  connections,
  setConnections,
  allScenes,
  canAddChild,
  maxChildren,
  currentChildren,
  setEditorOpen,
  onWillTemporarilyClose,
}) {
  const listRef = React.useRef(null);
  const graph = React.useContext(GraphCtx);
  const allSceneOptions = Array.isArray(allScenes) ? allScenes : [];
  const allSceneIds = React.useMemo(
    () => new Set(allSceneOptions.map((s) => String(s.id))),
    [allSceneOptions]
  );

  // ★ Auto-resanitize when scene list changes (prevents stale targets)
  //   Important: do NOT call previewSceneConnections here, otherwise we
  //   create a feedback loop (graph -> ctx -> effect -> graph ...).
  React.useEffect(() => {
    setConnections((prev) =>
      sanitizeConnections(prev, {
        maxChildren,
        selfId: id,
        allSceneIds,
      })
    );
  }, [allSceneIds, maxChildren, id, setConnections]);



  // Capacity rules:
  const plannedExisting = (connections || []).filter(
    (c) => c.type === "existing" && c.childId
  ).length;
  const plannedNew = (connections || []).filter((c) => c.type === "new").length;
  const plannedTotal = plannedExisting + plannedNew;

  const remainingRows = Math.max(0, maxChildren - (connections?.length || 0));
  const canAddRow = remainingRows > 0;

  // Keep both a clamped and raw delta
  const remainingTotal = Math.max(0, maxChildren - plannedTotal);
  const capacityDelta = maxChildren - plannedTotal;

  // ✅ Chip color should be green even when we're exactly at capacity (delta === 0)
  //    Remove the row gate from here; this is about validity, not adding more rows.
  const canAddNew = capacityDelta >= 0 && !!canAddChild;

  const addConnection = () => {
    if (!canAddRow) return;
    setConnections((prev) => {
      const next = [
        ...(prev || []),
        withStableKey({ type: "new", childId: "", text: "" }),
      ];
      const cleaned = sanitizeConnections(next, {
        maxChildren,
        selfId: id,
        allSceneIds,
      });
      graph?.previewSceneConnections?.(id, cleaned);
      return cleaned;
    });
  };

  const updateConnection = (idx, patch) => {
    setConnections((prev) => {
      const next = [...(prev || [])];
      next[idx] = withStableKey({ ...next[idx], ...patch });
      const cleaned = sanitizeConnections(next, {
        maxChildren,
        selfId: id,
        allSceneIds,
      });
      graph?.previewSceneConnections?.(id, cleaned);
      return cleaned;
    });
  };

  const removeByKey = React.useCallback(
    (rowKey) => {
      setConnections((prev) => {
        const filtered = (prev || []).filter(
          (c) => (c.__key || "") !== rowKey
        );
        const cleaned = sanitizeConnections(filtered, {
          maxChildren,
          selfId: id,
          allSceneIds,
        });
        graph?.previewSceneConnections?.(id, cleaned);
        return cleaned;
      });
    },
    [setConnections, maxChildren, id, allSceneIds, graph]
  );


  const {
    schedule: scheduleDelete,
    undo: undoDelete,
    isPending,
  } = useDeferredDeletion(listRef, removeByKey, "data-conn-key");

  const move = (from, to) => {
    const beforeRects = measureListChildren(listRef, "data-conn-key");
    setConnections((prev) => {
      const list = [...(prev || [])];
      if (to < 0 || to >= list.length) return list;
      const [row] = list.splice(from, 1);
      list.splice(to, 0, row);
      const cleaned = sanitizeConnections(list, {
        maxChildren,
        selfId: id,
        allSceneIds,
      });
      graph?.previewSceneConnections?.(id, cleaned);
      return cleaned;
    });

    flipAnimateAll(listRef, "data-conn-key", beforeRects);
  };



  const showChoiceText = (connections?.length || 0) > 1;

  const beginGraphPick = (idx, chosenIds) => {
    if (!graph?.beginPick) return;

    // Always switch to graph view while picking
    graph.ensureGraphView?.();

    onWillTemporarilyClose?.(); // keep current form state
    setEditorOpen?.(false);     // hide dialog to interact with the graph

    const reopenEditor = () => {
      let reopened = false;
      const reopenOnce = () => {
        if (reopened) return;
        reopened = true;
        setEditorOpen?.(true);
      };
      // double rAF + timeout to avoid focus/flicker races
      requestAnimationFrame(() => requestAnimationFrame(reopenOnce));
      setTimeout(reopenOnce, 250);
    };

    graph.beginPick(
      id,
      (targetId) => {
        const tid = String(targetId);
        const invalid =
          tid === String(id) ||
          chosenIds.has(tid) ||
          (graph?.isDescendant?.(tid, id) ?? false);

        if (!invalid) {
          updateConnection(idx, { type: 'existing', childId: tid });
        }

        // Always reopen, even after an invalid pick.
        reopenEditor();
      },
      {
        extraBlockedIds: Array.from(chosenIds),
        onCancel: reopenEditor,
      }
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ fontWeight: 600, mb: 1 }}>{t`Connections`}</Box>

      <Box ref={listRef}>
        {(connections || []).map((c, idx) => {
          const useExisting = c.type === "existing";
          const connKey =
            c.__key || `${c.type || "new"}|${c.childId || ""}|${c.text || ""}|${idx}`;

          // 👉 single stable key used everywhere
          const rowKey = c.__key || connKey;
          const pending = isPending(rowKey);


          const textValue = c.text || "";
          const trimmedText = textValue.trim();
          const hasChild = useExisting && !!c.childId;

          // blocking: existing child selected but no text
          const isBlockingEmpty = hasChild && trimmedText.length === 0;

          // warning: in choices-mode with empty text (but no existing child yet)
          const isWarningEmpty =
            !hasChild && trimmedText.length === 0 && showChoiceText;

          // Disable selecting a child that’s already chosen in another row
          const chosenIds = new Set(
            (connections || [])
              .map((x, i) =>
                i === idx ? null : x.type === "existing" ? x.childId : null
              )
              .filter(Boolean)
              .map(String)
          );

          const rowEmptyError = isBlockingEmpty || isWarningEmpty;
          const baseChoiceLabel = t`Choice ${idx + 1} text`;
          const choiceLabel = isBlockingEmpty
            ? t`Choice ${idx + 1} text – required for linked scene`
            : isWarningEmpty
              ? t`Choice ${idx + 1} text – should not be empty`
              : baseChoiceLabel;


          return (
            <Box
              key={connKey}
              data-conn-key={rowKey}
              sx={(t) => ({
                display: "grid",
                gridTemplateColumns: showChoiceText
                  ? { xs: "1fr", sm: "1fr 160px 1fr auto" }
                  : { xs: "1fr", sm: "160px 1fr auto" },
                gap: { xs: 1, sm: 1.25 },
                mb: 1.25,
                p: 1,
                borderRadius: 1,
                borderWidth: 1,
                borderStyle: "solid",
                // 🔴 outline only, no fill when warning/error
                borderColor: rowEmptyError
                  ? t.palette.error.main
                  : t.palette.divider,
                bgcolor: "transparent",
                alignItems: "center",
                opacity: pending ? 0.65 : 1,
                transform: pending ? "translateX(6px)" : "none",
                transition:
                  "opacity 160ms ease, transform 160ms ease, border-color 160ms ease",
              })}
            >
              {showChoiceText && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    size="small"
                    // ⚠️ Warning copy lives in label/placeholder (shrinks with outline)
                    label={choiceLabel}
                    placeholder={
                      isBlockingEmpty
                        ? t`Required for linked scene`
                        : isWarningEmpty
                          ? t`Should not be empty`
                          : ""
                    }

                    margin="dense"
                    value={textValue}
                    onChange={(e) =>
                      updateConnection(idx, {
                        text: e.target.value.slice(0, 60),
                      })
                    }
                    error={isBlockingEmpty}
                    sx={(t) => ({
                      flexGrow: 1,
                      minWidth: 0,
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: isBlockingEmpty
                            ? t.palette.error.main
                            : isWarningEmpty
                              ? t.palette.warning.main
                              : t.palette.divider,
                        },
                        "&:hover fieldset": {
                          borderColor: isBlockingEmpty
                            ? t.palette.error.dark
                            : isWarningEmpty
                              ? t.palette.warning.dark
                              : t.palette.text.secondary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: isBlockingEmpty
                            ? t.palette.error.dark
                            : isWarningEmpty
                              ? t.palette.warning.dark
                              : t.palette.callToAction?.main ||
                              t.palette.primary.main,
                          borderWidth: 2,
                        },
                      },
                    })}
                    inputProps={{ maxLength: 60 }}
                    disabled={pending}
                  />
                  <Typography
                    variant="caption"
                    sx={(t) => ({
                      flexShrink: 0,
                      minWidth: 52,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: isBlockingEmpty
                        ? t.palette.error.main
                        : isWarningEmpty
                          ? t.palette.warning.main
                          : t.palette.text.secondary,
                    })}
                  >
                    {textValue.length}/60
                  </Typography>
                </Box>
              )}

              {/* Target type as toggles — allow switching both ways */}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={c.type || "new"}
                onChange={(_, val) => {
                  if (!val) return;
                  updateConnection(idx, {
                    type: val,
                    ...(val === "new" ? { childId: "" } : {}),
                  });
                }}
                sx={{
                  justifySelf: { xs: "stretch", sm: "start" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <ToggleButton value="new">{t`New`}</ToggleButton>
                <ToggleButton value="existing">{t`Existing`}</ToggleButton>

              </ToggleButtonGroup>

              {useExisting ? (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr auto", sm: "1fr auto" },
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label={t`Child scene`}
                      margin="dense"
                      value={c.childId || ""}
                      onChange={(e) =>
                        updateConnection(idx, { childId: e.target.value })
                      }
                      sx={{ minWidth: 0 }}
                      disabled={pending}
                    >
                      {allSceneOptions
                        .filter((s) => s.id !== id)
                        .map((s) => (
                          <MenuItem
                            key={s.id}
                            value={s.id}
                            disabled={
                              chosenIds.has(String(s.id)) ||
                              (graph?.isDescendant?.(s.id, id) ?? false) ||
                              String(s.id) === String(id)
                            }
                          >
                            {s.title}
                          </MenuItem>
                        ))}
                    </TextField>

                    {/* Map icon */}
                    <IconButton
                      size="small"
                      title={t`Pick on graph`}
                      onClick={() => beginGraphPick(idx, chosenIds)}
                      disabled={pending}
                      sx={{
                        justifySelf: "end",
                        alignSelf: "center",
                        width: 28,
                        height: 28,
                        display: { xs: "none", sm: "inline-flex" },
                        color: "text.primary",
                        "&.Mui-disabled": { color: "text.disabled" },
                      }}
                    >
                      <MapIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Chip
                    size="small"
                    color={canAddNew ? "success" : "default"}
                    label={
                      canAddNew
                        ? capacityDelta === 0
                          ? t`Will create (fills capacity)`
                          : t`Will create new scene`
                        : t`Child limit reached`
                    }
                  />

                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                  mt: { xs: 0.25, sm: 0 },
                  alignItems: "center",
                }}
              >
                {useExisting && (
                  <IconButton
                    size="small"
                    title="Pick on graph"
                    onClick={() =>
                      beginGraphPick(
                        idx,
                        new Set(
                          (connections || [])
                            .map((x, i) =>
                              i === idx
                                ? null
                                : x.type === "existing"
                                  ? x.childId
                                  : null
                            )
                            .filter(Boolean)
                            .map(String)
                        )
                      )
                    }
                    disabled={pending}
                    sx={{
                      display: { xs: "inline-flex", sm: "none" },
                      width: 28,
                      height: 28,
                      color: "text.primary",
                      "&.Mui-disabled": { color: "text.disabled" },
                    }}
                  >
                    <MapIcon fontSize="inherit" />
                  </IconButton>
                )}

                <IconButton
                  size="small"
                  title="Move up"
                  onClick={() => move(idx, idx - 1)}
                  disabled={pending}
                  sx={{
                    color: "text.primary",
                    "&.Mui-disabled": { color: "text.disabled" },
                  }}
                >
                  <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>

                <IconButton
                  size="small"
                  title="Move down"
                  onClick={() => move(idx, idx + 1)}
                  disabled={pending}
                  sx={{
                    color: "text.primary",
                    "&.Mui-disabled": { color: "text.disabled" },
                  }}
                >
                  <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>

                {!pending ? (
                  <IconButton
                    size="small"
                    title="Remove"
                    onClick={() => scheduleDelete(rowKey)}
                    sx={{
                      color: "text.primary",
                      "&.Mui-disabled": { color: "text.disabled" },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="inherit" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    title="Undo delete"
                    onClick={() => undoDelete(rowKey)}
                    sx={{
                      color: "text.primary",
                      "&.Mui-disabled": { color: "text.disabled" },
                    }}
                  >
                    <UndoIcon fontSize="inherit" />
                  </IconButton>
                )}
              </Box>

              {pending && <DeletingBar />}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={addConnection}
          disabled={!canAddRow}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {t`Add connection`}
        </Button>
        <Chip
          size="small"
          label={t`Rows left: ${remainingRows} • Total left: ${remainingTotal}`}
        />

      </Box>
      {!!currentChildren && (
        <Box sx={{ mt: 1.25 }}>
          <Chip
            size="small"
            variant="outlined"
            label={t`Existing edges in graph: ${currentChildren}`}
          />

        </Box>
      )}
    </Box>
  );
}


// ───────────────────────────────────────────────────────────────────────────────
// Scene Node
// ───────────────────────────────────────────────────────────────────────────────
export default function SceneNode({ id, data, selected }) {
  const graph = React.useContext(GraphCtx);
  const [open, setOpen] = React.useState(false);

  const [title, setTitle] = React.useState(data.title || '');
  const [background, setBackground] = React.useState(
    Number.isFinite(data.background) ? data.background : 0
  );
  const [lines, setLines] = React.useState(
    Array.isArray(data.lines) ? data.lines : []
  );
  const [connections, setConnections] = React.useState(
    () => deriveConnectionsFromData(data)
  );

  const isStartScene = !!data.entryScene;
  const isEndScene = !!data.lastScene;

  const isTitleEditable = !isStartScene && !isEndScene;
  const titleTrimmed = trimStr(title);
  const isTitleEmpty = isTitleEditable && titleTrimmed.length === 0;

  const childCount = graph?.getChildCount?.(id) ?? 0;
  const maxChildren = graph?.maxChildren ?? 3;
  const [saving, setSaving] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = React.useState(false);

  const parentCount = graph?.getParentCount?.(id) ?? 0;

  const skipNextRehydrateRef = React.useRef(false);
  const requestSkipNextRehydrate = React.useCallback(() => {
    skipNextRehydrateRef.current = true;
  }, []);

  const [initialSnapshot, setInitialSnapshot] = React.useState(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const highlight = graph?.getHighlight?.(id) || 'none';
  const hasSelection = !!graph?.hasSelection;

  // 🔍 Validation info from GraphCtx
  const validation = graph?.getValidation?.(id) || null;
  const validationStatus = validation?.status || 'ok'; // 'blocking' | 'warning' | 'ok'
  const hasValidationOutline =
    validationStatus === 'blocking' || validationStatus === 'warning';

  const handleCreateCharacter = React.useCallback(async (payload) => {
    const storyID = graph?.storyID;
    if (!storyID) return null;
    const created = await addCharacter(storyID, payload);
    if (typeof graph?.refreshCharacters === 'function') {
      await graph.refreshCharacters();
    } else {
      try {
        const fresh = await getCharacters(storyID);
        if (typeof graph?.setCharacters === 'function') graph.setCharacters(fresh);
      } catch { /* non-fatal */ }
    }
    return created;
  }, [graph]);

  // Auto-open when the node is created with data.autoOpen (used by global "New Scene" button)
  React.useEffect(() => {
    if (data?.autoOpen && !open) {
      // Open the editor dialog for this new scene
      setOpen(true);

      // Clear the flag so it only auto-opens once
      if (graph?.updateScene) {
        graph.updateScene(id, { autoOpen: false });
      }
    }
  }, [data?.autoOpen, open, graph, id]);

  // Re-hydrate form every time the dialog opens, using the latest node data
  React.useEffect(() => {
    if (!open) return;

    if (skipNextRehydrateRef.current) {
      skipNextRehydrateRef.current = false;
      return;
    }

    const nextTitle = data.title || '';
    const nextBackground = Number.isFinite(data.background) ? data.background : 0;
    const nextLines = Array.isArray(data.lines) ? data.lines : [];
    const nextConnections = deriveConnectionsFromData(data);

    setTitle(nextTitle);
    setBackground(nextBackground);
    setLines(nextLines);
    setConnections(nextConnections);

    // baseline snapshot for dirty detection
    setInitialSnapshot({
      title: nextTitle,
      background: nextBackground,
      lines: nextLines,
      connections: nextConnections,
    });
  }, [open]);

  // Is the form different from the baseline snapshot?
  const isDirty = React.useMemo(() => {
    if (!initialSnapshot) return false;
    try {
      const current = { title, background, lines, connections };
      return JSON.stringify(current) !== JSON.stringify(initialSnapshot);
    } catch {
      return false;
    }
  }, [initialSnapshot, title, background, lines, connections]);

  const handleRequestClose = React.useCallback(() => {
    if (saving) return;
    if (isDirty) {
      setDiscardDialogOpen(true);
    } else {
      setOpen(false);
    }
  }, [saving, isDirty]);

  // Warn when closing the tab/window with unsaved changes
  React.useEffect(() => {
    if (!open || !isDirty) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [open, isDirty]);


  const saveChanges = async () => {
    if (saving) return;
    setSaving(true);

    const safeTitle = trimStr(title);
    const bg = asInt(background, 0);
    const safeLines = Array.isArray(lines)
      ? lines.map(l => ({
        id: l.id || '',
        speaker: trimStr(l.speaker) || 'Narrator',
        text: trimStr(l.text) || '',
      }))
      : [];

    const allScenes = (graph?.getAllScenes?.() || []).map(s => ({ id: String(s.id), title: s.title }));
    const allSceneIds = new Set(allScenes.map(s => s.id));
    const cleanedConnections = sanitizeConnections(connections, {
      maxChildren,
      selfId: String(id),
      allSceneIds,
    });

    const payload = { title: safeTitle, background: bg, lines: safeLines };
    if (cleanedConnections.length === 0) {
      payload.mode = 'none';
    } else if (cleanedConnections.length === 1) {
      payload.mode = 'continue';
      const c0 = cleanedConnections[0];
      payload.continue =
        c0.type === 'existing' && c0.childId
          ? { type: 'existing', childId: String(c0.childId) }
          : { type: 'new' };
    } else {
      payload.mode = 'choices';
      payload.choices = cleanedConnections.slice(0, 3).map(c => ({
        text: c.text || '',
        ...(c.type === 'existing' && c.childId
          ? { type: 'existing', childId: String(c.childId) }
          : { type: 'new' }),
      }));
    }

    graph?.updateScene?.(id, { title: safeTitle });

    try {
      await graph?.saveScene?.(id, payload);
      setOpen(false);
    } catch (e) {
      console.error('Failed to save scene:', e);
    } finally {
      setSaving(false);
    }
  };

  const hiddenTargetStyle = { width: 0, height: 0, opacity: 0, pointerEvents: 'none' };
  const hiddenSourceStyle = { width: 0, height: 0, opacity: 0, pointerEvents: 'none' };

  return (
    <>
      <Box
        sx={{
          p: 1.25,
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: 1,
          minWidth: 180,
          maxWidth: 200, // keep width cap
          border: (theme) => {
            const st = graph?.getPickState?.(id) || 'none';
            const baseColor =
              st === 'ok'
                ? theme.palette.success.main
                : st === 'no'
                  ? theme.palette.error.main
                  : theme.palette.divider;

            if (highlight === 'selected') {
              return `2px solid ${theme.palette.callToAction?.main || theme.palette.primary.main
                }`;
            }
            if (highlight === 'parent' || highlight === 'child') {
              return `2px solid ${theme.palette.primary.main}`;
            }
            return `1px solid ${baseColor}`;
          },

          // 🔴🟠 outer validation ring
          outline: (theme) =>
            hasValidationOutline
              ? `2px solid ${validationStatus === 'blocking'
                ? theme.palette.error.main // blocking = red
                : theme.palette.warning.main // warning/inconsistent = orange
              }`
              : 'none',
          outlineOffset: 2,

          position: 'relative',
          cursor:
            (graph?.getPickState?.(id) || 'none') === 'none'
              ? 'pointer'
              : 'crosshair',
          opacity: hasSelection && highlight === 'none' ? 0.45 : 1,
          transition:
            'opacity 140ms ease, border-color 140ms ease, box-shadow 140ms ease',
        }}
        onClick={(e) => {
          const st = graph?.getPickState?.(id) || 'none';
          if (st === 'none') {
            e.stopPropagation();
            setOpen(true);
          }
        }}
      >


        {/* Title */}
        <Box
          sx={{
            fontWeight: 700,
            fontSize: 14,
            lineHeight: 1.2,
            maxWidth: '100%',
            wordBreak: 'break-word',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {title}
        </Box>

        {/* Line count */}
        <Box sx={{ fontSize: 12, opacity: 0.8, mt: 0.25 }}>
          {Array.isArray(lines) ? lines.length : 0}{" "}
          {Array.isArray(lines) && lines.length === 1 ? t`line` : t`lines`}
        </Box>

        {/* Minimal visible handles (no + buttons) */}
        <Handle
          type="target"
          position={Position.Top}
          style={
            isStartScene
              ? hiddenTargetStyle
              : {
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#bdbdbd',
                border: '2px solid #fff',
                pointerEvents: 'auto',
              }
          }
        />
        <Handle
          type="source"
          position={Position.Bottom}
          style={
            isEndScene
              ? hiddenSourceStyle
              : {
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#bdbdbd',
                border: '2px solid #fff',
                pointerEvents: 'auto',
              }
          }
        />
      </Box>

      {/* Editor Dialog */}
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (saving) return;
          handleRequestClose();
        }}
        disableEnforceFocus
        disableEscapeKeyDown={saving}
        fullWidth
        maxWidth="md"
        fullScreen={fullScreen}
        keepMounted
        scroll="paper"
      >

        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box component="span">{t`Edit Scene`}</Box>
            {/* Don’t allow deleting START/END here */}
            {!isStartScene && !isEndScene && (
              <IconButton
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                sx={(t) => ({
                  ml: 1,
                  color: t.palette.text.primary,
                  '&:hover': {
                    backgroundColor: t.palette.action.hover,
                    color: t.palette.text.primary,
                  },
                })}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>

            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label={isTitleEditable ? t`Title *` : t`Title`}
            margin="dense"
            value={title}
            onChange={(e) => {
              const v = e.target.value.slice(0, 40); // 40-char hard cap
              setTitle(v);
            }}
            disabled={!isTitleEditable}
            error={isTitleEmpty}
            helperText={
              isStartScene
                ? t`Start scene title is fixed.`
                : isEndScene
                  ? t`End scene title is fixed.`
                  : isTitleEmpty
                    ? t`Title is empty – this scene will be harder to find in the list.`
                    : `${title.length}/40`
            }
            inputProps={{ maxLength: 40 }}
            FormHelperTextProps={{
              sx: (t) => ({
                minHeight: 20,
                display: 'flex',
                alignItems: 'center',
                // keep color logic if you want later – for now neutral
                color: t.palette.text.secondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }),
            }}
            sx={(t) => ({
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: isTitleEmpty
                    ? t.palette.error.main
                    : t.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: isTitleEmpty
                    ? t.palette.error.dark
                    : t.palette.text.secondary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: isTitleEmpty
                    ? t.palette.error.dark
                    : t.palette.callToAction?.main || t.palette.primary.main,
                  borderWidth: 2,
                },
              },
            })}
          />


          <BackgroundSelect value={background} onChange={setBackground} />

          <LinesEditor
            lines={lines}
            setLines={setLines}
            characters={graph?.characters || []}
            onCreateCharacter={handleCreateCharacter}
          />
          {!isEndScene && (
            <ConnectionsEditor
              id={id}
              connections={connections}
              setConnections={setConnections}
              allScenes={(graph?.getAllScenes?.() || []).filter(s => String(s.id) !== String(id))}
              canAddChild={graph?.canAddChildFrom?.(id) ?? true}
              maxChildren={maxChildren}
              currentChildren={childCount}
              setEditorOpen={setOpen}
              onWillTemporarilyClose={requestSkipNextRehydrate}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{ p: { xs: 1, sm: 2 }, flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 1, sm: 0 } }}
        >
          <Button
            onClick={handleRequestClose}
            disabled={saving}
            sx={(t) => ({
              flex: { xs: '1 1 45%', sm: '0 0 auto' },
              color: t.palette.text.primary,
            })}
          >
            {t`Cancel`}
          </Button>

          <Button
            variant="contained"
            onClick={saveChanges}
            disabled={saving}
            sx={{ flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
          >
            {saving ? t`Saving…` : t`Save`}
          </Button>
        </DialogActions>

      </Dialog>
      {/* Delete dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleting) return;
          setDeleteDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t`Delete scene`}</DialogTitle>
        <DialogContent dividers>
          {parentCount > 0 || childCount > 0 ? (
            <Typography variant="body2">
              {t`This scene is connected to other scenes. Please remove all incoming and outgoing connections before deleting it.`}
            </Typography>
          ) : (
            <Typography variant="body2">
              {t`Are you sure you want to delete this scene? This action cannot be undone.`}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            sx={(t) => ({
              color: t.palette.text.primary,
            })}
          >
            {parentCount > 0 || childCount > 0 ? t`Close` : t`Cancel`}
          </Button>

          {parentCount === 0 && childCount === 0 && (
            <Button
              color="error"
              variant="contained"
              disabled={deleting}
              onClick={async () => {
                if (deleting) return;
                try {
                  setDeleting(true);
                  await graph?.deleteScene?.(id);
                  setDeleteDialogOpen(false);
                  setOpen(false); // close editor as well
                } catch (e) {
                  console.error('Failed to delete scene:', e);
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? t`Deleting…` : t`Delete`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Discard unsaved changes dialog */}
      <Dialog
        open={discardDialogOpen}
        onClose={() => setDiscardDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t`Discard changes?`}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {t`You have unsaved changes in this scene. If you close now, your edits will be lost.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDiscardDialogOpen(false)}
            sx={(t) => ({
              color: t.palette.text.primary,
            })}
          >
            {t`Keep editing`}
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              setDiscardDialogOpen(false);
              setOpen(false);
              try {
                await graph?.refreshFromBackend?.();
              } catch {
                // non-fatal; worst case graph stays in preview state
              }
            }}
          >
            {t`Discard`}
          </Button>

        </DialogActions>
      </Dialog>

    </>
  );
}
